import { ArrayUtil, LogManager } from "@mcleod/core";
import { Layout } from "../components/layout/Layout";
import { Table } from "../components/table/Table";
import { TableRow } from "../components/table/TableRow";
import { DataSource, DataSourceAction } from "../databinding/DataSource";
import { DataSourceExecutionEvent } from "../events/DataSourceExecutionEvent";

const log = LogManager.getLogger("components.page.ListDrilldownHandler");

export class ListDrilldownHandler<T extends Layout = Layout> {
    protected keyValues: string[];
    protected layout: T;
    protected table: Table;
    protected dataSource: DataSource;
    protected keyField: string;
    protected searchFilter: any;

    constructor(keyValues: string[], layout: T) {
        this.keyValues = keyValues;
        this.layout = layout;
    }

    protected async getTableFromLayout(): Promise<Table> {
        return this.layout?.getRecursiveChildren().find(c => c.serializationName === "table") as Table;
    }

    protected canSearchTable(): boolean {
        if (ArrayUtil.isEmptyArray(this.keyValues)) {
            log.debug("No search filter provided for layout: %s", this.layout.layoutName);
            return false;
        }
        if (this.table == null) {
            log.debug("Unable to find table in layout: %s", this.layout.layoutName);
            return false;
        }
        if (this.dataSource == null) {
            log.debug("table: %s in layout: %s has no dataSource", this.table.id, this.layout.layoutName);
            return false;
        }
        if (this.keyField == null) {
            log.debug("dataSource: %s in layout: %s has no key field", this.dataSource.id, this.layout.layoutName);
            return false;
        }
        return true;
    }

    protected get searchWasExecuted(): boolean {
        return this.dataSource?.rowCount > 0 && this.dataSource?.lastSearch != null;
    }

    async execute() {
        this.table = await this.getTableFromLayout();
        this.dataSource = this.table?.dataSource;
        this.keyField = this.dataSource?.getMetadataFromCache()?.keyFields?.[0];
        const searchValue = this.keyValues.length > 1 ? `in ${this.keyValues.join(",")}` : this.keyValues[0];
        this.searchFilter = { [this.keyField]: searchValue };

        if (this.keyValues.length > 1) {
            await this.handleMultiValue();
        } else {
            await this.handleSingleValue();
        }
    }

    protected async handleMultiValue() {
        await this.ensureTableSearched();
    }

    protected async handleSingleValue() {
        await this.ensureTableSearched();
        await this.doubleClickRow(this.keyValues[0]);
    }

    /**
     * Ensures the table has completed a search. Resolves immediately if already searched.
     */
    protected ensureTableSearched(): Promise<void> {
        return new Promise((resolve) => {
            if (this.canSearchTable() === false || this.searchWasExecuted) {
                resolve();
                return;
            }

            const executionListener = (event: DataSourceExecutionEvent) => {
                if (event.getAction() === DataSourceAction.SEARCH) {
                    this.dataSource.removeAfterExecutionListener(executionListener);
                    resolve();
                }
            };

            this.dataSource.addAfterExecutionListener(executionListener);

            if (this.dataSource.actionInProgress !== DataSourceAction.SEARCH) {
                this.dataSource.search(this.searchFilter);
            }
        });
    }

    protected findRowInTable(keyValue: string): TableRow {
        return this.table.getRowByDataKeyValue(keyValue);
    }

    /**
     * Simulates a double-click on a row and wait for the expansion animation to finish.
     */
    protected doubleClickRow(key: string): Promise<void> {
        const tableRow = this.findRowInTable(key);

        return new Promise((resolve) => {
            if (tableRow == null) {
                resolve();
                return;
            }

            tableRow.populateDOMIfNeeded().then(() => {
                tableRow.doubleClicked(new window.MouseEvent('dblclick', {
                    cancelable: true,
                    bubbles: true,
                    detail: 2,
                }));

                // small delay to allow the layout expansion animation to finish
                setTimeout(() => {
                    tableRow.addMountListener(() => {
                        requestAnimationFrame(() => tableRow.scrollIntoView());
                    });
                    resolve();
                }, 150);
            });
        });
    }
}
